import React from 'react';
import { HeroSection } from '../../components/HeroSection';
import { Button } from '../../components/ui/Button';
import { BookOpen, Sparkles, Heart, PenTool, ArrowRight, Calendar, Lightbulb, Users, Mail } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
export function ReadWaestm() {
  const futureThemes = [{
    title: 'Fat Bodies in Public Space',
    description: 'Architecture, transport, seating, and the built environment as tools of exclusion.',
    color: 'border-brand-orange'
  }, {
    title: 'Medical Violence & Fat Health',
    description: 'Diagnostic overshadowing, weight-based denial of care, and reclaiming bodily autonomy in healthcare.',
    color: 'border-brand-magenta'
  }, {
    title: 'Fat Childhood & Education',
    description: 'BMI report cards, PE trauma, school uniforms, and what fat-affirming education could look like.',
    color: 'border-brand-cyan'
  }, {
    title: 'Desire, Intimacy & Fat Love',
    description: 'Dating while fat, fetishisation, desirability politics, and the radical act of fat pleasure.',
    color: 'border-brand-lime'
  }, {
    title: 'Fat Work & Economic Justice',
    description: 'Hiring discrimination, workplace hostility, dress codes, and the financial cost of being fat.',
    color: 'border-brand-orange'
  }, {
    title: 'Intersections: Race, Disability & Fatness',
    description: 'How anti-fatness compounds with racism, ableism, transphobia, and poverty.',
    color: 'border-brand-magenta'
  }];
  return <div className="min-h-screen bg-brand-cream" data-id="element-1581">
      <HeroSection title="Read WAESTM" subtitle="A new publication exploring fat liberation, anti-fat bias, and the politics of living in a fat body." tag="The Digital Publication" bgColor="orange" data-id="element-1582" />

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1583">
        <div className="prose prose-lg" data-id="element-1584">
          <p className="text-xl text-gray-700 leading-relaxed" data-id="element-1585">
            <strong className="text-brand-orange" data-id="element-1586">WAESTM</strong> is a digital
            publication by ReGrowth — a space for essays, reporting, poetry,
            visual work, and critical thought rooted in fat justice. We're
            building something that centres fat voices, takes ideas seriously,
            and refuses to water down the politics.
          </p>
          <p className="text-lg text-gray-600 mt-4" data-id="element-1587">
            We're not live yet. But we're close. Here's what's coming.
          </p>
        </div>
      </section>

      {/* Upcoming First Issue */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-1588">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="border-2 border-brand-black bg-white overflow-hidden" data-id="element-1589">
          <div className="grid grid-cols-1 lg:grid-cols-2" data-id="element-1590">
            {/* Left: Visual */}
            <div className="bg-brand-orange p-12 flex flex-col justify-center relative overflow-hidden" data-id="element-1591">
              {/* Decorative circles */}
              <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-white/10" data-id="element-1592" />
              <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-white/5" data-id="element-1593" />

              <div className="relative z-10" data-id="element-1594">
                <span className="inline-block px-3 py-1 mb-6 text-xs font-bold uppercase tracking-widest bg-white text-brand-orange" data-id="element-1595">
                  Issue One
                </span>
                <h2 className="text-4xl md:text-5xl font-black uppercase text-white leading-[0.95] mb-6" data-id="element-1596">
                  The First Issue is Coming
                </h2>
                <div className="flex items-center gap-3 text-white/80" data-id="element-1597">
                  <Calendar className="h-5 w-5" data-id="element-1598" />
                  <span className="font-medium" data-id="element-1599">Launching 2025</span>
                </div>
              </div>
            </div>

            {/* Right: Details */}
            <div className="p-12" data-id="element-1600">
              <h3 className="text-2xl font-black uppercase mb-6 text-brand-black" data-id="element-1601">
                What to Expect
              </h3>
              <div className="space-y-6 text-gray-700 leading-relaxed" data-id="element-1602">
                <p data-id="element-1603">
                  Our debut issue will set the tone for everything WAESTM is
                  about: sharp analysis, lived experience, creative risk, and
                  unapologetic fat politics.
                </p>
                <p data-id="element-1604">
                  We're commissioning essays, personal narratives, poetry, and
                  visual work from fat creators — especially fat Black,
                  Indigenous, disabled, trans, and working-class voices.
                </p>
              </div>

              <div className="mt-8 space-y-4" data-id="element-1605">
                <div className="flex items-start gap-3" data-id="element-1606">
                  <Sparkles className="h-5 w-5 text-brand-orange flex-shrink-0 mt-1" data-id="element-1607" />
                  <p className="text-sm text-gray-700" data-id="element-1608">
                    Original essays and critical writing on fat liberation
                  </p>
                </div>
                <div className="flex items-start gap-3" data-id="element-1609">
                  <PenTool className="h-5 w-5 text-brand-orange flex-shrink-0 mt-1" data-id="element-1610" />
                  <p className="text-sm text-gray-700" data-id="element-1611">
                    Poetry and creative work that centres fat embodiment
                  </p>
                </div>
                <div className="flex items-start gap-3" data-id="element-1612">
                  <BookOpen className="h-5 w-5 text-brand-orange flex-shrink-0 mt-1" data-id="element-1613" />
                  <p className="text-sm text-gray-700" data-id="element-1614">
                    Investigative reporting on systems that harm fat people
                  </p>
                </div>
                <div className="flex items-start gap-3" data-id="element-1615">
                  <Users className="h-5 w-5 text-brand-orange flex-shrink-0 mt-1" data-id="element-1616" />
                  <p className="text-sm text-gray-700" data-id="element-1617">
                    Conversations and interviews with fat activists and thinkers
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Future Themes */}
      <section className="py-20 bg-white" data-id="element-1618">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1619">
          <div className="max-w-3xl mb-12" data-id="element-1620">
            <h2 className="text-3xl font-black uppercase mb-4 text-brand-black" data-id="element-1621">
              Future Themes
            </h2>
            <p className="text-lg text-gray-700" data-id="element-1622">
              Each issue of WAESTM will explore a theme through multiple lenses
              — essays, art, reporting, and lived experience. Here's what we're
              planning.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1623">
            {futureThemes.map((theme, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.08
          }} className={`border-l-4 ${theme.color} bg-brand-cream p-6`} data-id="element-1624">
                <div className="flex items-start gap-3 mb-3" data-id="element-1625">
                  <Lightbulb className="h-5 w-5 text-brand-orange flex-shrink-0 mt-0.5" data-id="element-1626" />
                  <h3 className="text-lg font-black uppercase text-brand-black leading-tight" data-id="element-1627">
                    {theme.title}
                  </h3>
                </div>
                <p className="text-gray-700 text-sm leading-relaxed" data-id="element-1628">
                  {theme.description}
                </p>
              </motion.div>)}
          </div>

          <p className="text-sm text-gray-500 mt-8" data-id="element-1629">
            Themes are subject to change based on community input and
            contributor proposals. Have an idea?{' '}
            <Link to="/contact" className="text-brand-orange font-bold hover:underline" data-id="element-1630">
              Get in touch
            </Link>
            .
          </p>
        </div>
      </section>

      {/* Call for Contributors */}
      <section className="py-20 bg-brand-navy text-white" data-id="element-1631">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1632">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1633">
            <div data-id="element-1634">
              <span className="inline-block px-3 py-1 mb-6 text-xs font-bold uppercase tracking-widest bg-brand-orange text-white" data-id="element-1635">
                Open Call
              </span>
              <h2 className="text-3xl md:text-4xl font-black uppercase mb-6 leading-tight" data-id="element-1636">
                We Want to Hear From You
              </h2>
              <p className="text-xl text-gray-200 mb-6 leading-relaxed" data-id="element-1637">
                WAESTM is built by and for fat people. We're looking for
                writers, poets, artists, illustrators, photographers, and
                thinkers who want to contribute to our first and future issues.
              </p>
              <p className="text-gray-300 mb-8 leading-relaxed" data-id="element-1638">
                We especially encourage submissions from fat Black, Indigenous,
                disabled, trans, and working-class creators. You don't need to
                be a "professional writer" — we care about your voice, your
                ideas, and your craft.
              </p>
            </div>

            <div className="space-y-6" data-id="element-1639">
              <div className="bg-white/10 p-6 backdrop-blur-sm border border-white/20" data-id="element-1640">
                <h3 className="font-bold text-lg text-brand-orange mb-2" data-id="element-1641">
                  Essays & Criticism
                </h3>
                <p className="text-sm text-gray-200" data-id="element-1642">
                  Long-form writing that analyses culture, politics, systems,
                  and lived experience. 1,500–5,000 words.
                </p>
              </div>
              <div className="bg-white/10 p-6 backdrop-blur-sm border border-white/20" data-id="element-1643">
                <h3 className="font-bold text-lg text-brand-orange mb-2" data-id="element-1644">
                  Poetry & Creative Work
                </h3>
                <p className="text-sm text-gray-200" data-id="element-1645">
                  Poems, hybrid forms, and experimental writing that explores
                  fatness, embodiment, and liberation.
                </p>
              </div>
              <div className="bg-white/10 p-6 backdrop-blur-sm border border-white/20" data-id="element-1646">
                <h3 className="font-bold text-lg text-brand-orange mb-2" data-id="element-1647">
                  Visual Art & Photography
                </h3>
                <p className="text-sm text-gray-200" data-id="element-1648">
                  Illustration, photography, comics, and visual storytelling
                  that centres fat bodies and fat joy.
                </p>
              </div>
              <div className="bg-white/10 p-6 backdrop-blur-sm border border-white/20" data-id="element-1649">
                <h3 className="font-bold text-lg text-brand-orange mb-2" data-id="element-1650">
                  Reporting & Investigations
                </h3>
                <p className="text-sm text-gray-200" data-id="element-1651">
                  Investigative pieces that expose systems of oppression and
                  hold institutions accountable.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-6" data-id="element-1652">
            <p className="text-gray-300" data-id="element-1653">
              Interested? Send us a message with your idea, a writing sample (if
              you have one), and a short bio.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 bg-brand-orange text-white font-bold uppercase tracking-wide hover:bg-brand-magenta transition-colors flex-shrink-0" data-id="element-1654">
              Get in Touch <ArrowRight className="ml-2 h-4 w-4" data-id="element-1655" />
            </Link>
          </div>
        </div>
      </section>

      {/* Donate CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-id="element-1656">
        <div className="max-w-4xl mx-auto" data-id="element-1657">
          <div className="border-2 border-brand-black bg-white p-12 text-center" data-id="element-1658">
            <Heart className="h-12 w-12 text-brand-magenta mx-auto mb-6" data-id="element-1659" />
            <h2 className="text-3xl md:text-4xl font-black uppercase mb-6 text-brand-black" data-id="element-1660">
              Help Us Make WAESTM Happen
            </h2>
            <p className="text-lg text-gray-700 mb-4 max-w-2xl mx-auto leading-relaxed" data-id="element-1661">
              Running a publication takes resources. Your donation helps us pay
              contributors fairly, keep the publication free to read, and build
              something that lasts.
            </p>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto" data-id="element-1662">
              Every contribution — no matter the size — goes directly toward
              commissioning work, accessibility features, and keeping WAESTM
              independent.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8" data-id="element-1663">
              <a href="#" className="inline-flex items-center justify-center px-8 py-4 bg-brand-magenta text-white font-bold uppercase tracking-wide hover:bg-brand-magenta/90 transition-colors" data-id="element-1664">
                Donate to WAESTM
              </a>
              <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 border-2 border-brand-black text-brand-black font-bold uppercase tracking-wide hover:bg-brand-black hover:text-white transition-colors" data-id="element-1665">
                Sponsor an Issue
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12 pt-8 border-t border-gray-100 text-left" data-id="element-1666">
              <div data-id="element-1667">
                <p className="text-sm font-bold uppercase text-gray-500 mb-1" data-id="element-1668">
                  Your donation funds
                </p>
                <p className="text-sm text-gray-700" data-id="element-1669">
                  Fair pay for fat creators and contributors
                </p>
              </div>
              <div data-id="element-1670">
                <p className="text-sm font-bold uppercase text-gray-500 mb-1" data-id="element-1671">
                  Your donation funds
                </p>
                <p className="text-sm text-gray-700" data-id="element-1672">
                  Accessibility: captions, transcripts, alt text, screen reader
                  support
                </p>
              </div>
              <div data-id="element-1673">
                <p className="text-sm font-bold uppercase text-gray-500 mb-1" data-id="element-1674">
                  Your donation funds
                </p>
                <p className="text-sm text-gray-700" data-id="element-1675">
                  Keeping WAESTM free and independent — no corporate sponsors
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stay Updated */}
      <section className="py-20 bg-brand-orange text-white" data-id="element-1676">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1677">
          <Mail className="h-10 w-10 mx-auto mb-6 opacity-80" data-id="element-1678" />
          <h2 className="text-3xl font-black uppercase mb-6" data-id="element-1679">
            Be the First to Read Issue One
          </h2>
          <p className="text-xl mb-8 opacity-90" data-id="element-1680">
            Sign up and we'll let you know the moment WAESTM launches.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto" data-id="element-1681">
            <input type="email" placeholder="Your email" className="flex-1 px-4 py-3 text-brand-black border-2 border-white focus:outline-none" data-id="element-1682" />
            <button className="px-8 py-3 bg-white text-brand-orange font-bold uppercase tracking-wide hover:bg-gray-100 transition-colors" data-id="element-1683">
              Notify Me
            </button>
          </div>
        </div>
      </section>
    </div>;
}